import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../AuthContext";
import { publicTournamentFlow, publicTournamentRosters, subscribeTournamentFlow, tournamentFlow } from "../tournamentFlow";
import { supabase } from "../supabaseClient";
import "./ConfigurableTournament.css";
import logo from "../assets/logo.png";
import { teamArtworkSrc, teamAvatarSrc, teamDisplayMode } from "../tournamentTeamArtwork";
import TournamentNodeTree, { visibleStageIds } from "./TournamentNodeTree";
import TournamentAdminAccess from "./TournamentAdminAccess";
import { ReactComponent as IonFootball } from "../assets/tournament-ionicons/football-outline.svg";
import { ReactComponent as IonPodium } from "../assets/tournament-ionicons/podium-outline.svg";
import { ReactComponent as IonTime } from "../assets/tournament-ionicons/time-outline.svg";
import { ReactComponent as IonChevronBack } from "../assets/tournament-ionicons/chevron-back.svg";
import { ReactComponent as IonGitBranch } from "../assets/tournament-ionicons/git-branch-outline.svg";
import { ReactComponent as IonSettings } from "../assets/tournament-ionicons/settings-outline.svg";
import { ReactComponent as IonShare } from "../assets/tournament-ionicons/share-social-outline.svg";
import { ReactComponent as IonGrid } from "../assets/tournament-ionicons/grid-outline.svg";
import { ReactComponent as IonTrophy } from "../assets/tournament-ionicons/trophy-outline.svg";
import { ReactComponent as IonList } from "../assets/tournament-ionicons/list-outline.svg";
import { ReactComponent as IonFlask } from "../assets/tournament-ionicons/flask-outline.svg";
import { ReactComponent as IonPeople } from "../assets/tournament-ionicons/people-outline.svg";
import { ReactComponent as IonPersonAdd } from "../assets/tournament-ionicons/person-add-outline.svg";
import { ReactComponent as IonCalendar } from "../assets/tournament-ionicons/calendar-outline.svg";
import { ReactComponent as IonCalendarNumber } from "../assets/tournament-ionicons/calendar-number-outline.svg";
import { CalendarDays, ChevronLeft, CircleCheck, Flag, FlaskConical, Globe2, LayoutGrid, ListOrdered, Medal, RotateCcw, Shield, Smartphone, Trophy, UsersRound, X } from "lucide-react";

const clock = (minute) =>
  minute == null
    ? "Time flexible"
    : `${String(Math.floor(minute / 60) % 24).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`;
const whole = (value) =>
  /^\d+$/.test(String(value)) && Number.isSafeInteger(Number(value));
const avatarColors = [
  { backgroundColor: "#E7F2FF", color: "#07569F" },
  { backgroundColor: "#E6F5EF", color: "#087044" },
  { backgroundColor: "#FFF0DD", color: "#9A5A06" },
  { backgroundColor: "#F1EAFF", color: "#6643A0" },
];
const avatarStyle = (name) => avatarColors[Array.from(name || "").reduce((sum, letter) => sum + letter.charCodeAt(0), 0) % avatarColors.length];
const qualificationLabel = (spec) => {
  const entry = spec?.entry;
  if (!entry) return "All entrants start here";
  if (entry.method === "all") return "All entrants advance";
  if (entry.method === "manual") return `Admin selects ${spec.count}`;
  if (entry.method === "wins") return `${entry.min_wins} ${entry.min_wins === 1 ? "win" : "wins"} · ${spec.count} places`;
  if (entry.method === "ranks") return `Rank ${(entry.ranks || []).join(", ")}${entry.wildcard ? ` + ${entry.wildcard.count} best rank ${entry.wildcard.rank}` : ""}`;
  return "Qualified from earlier stage";
};
const Field = ({ label, ...props }) => (
  <label className="flow-field">
    {label}
    <input {...props} />
  </label>
);
const Check = ({ children, ...props }) => (
  <label className="flow-check">
    <input type="checkbox" {...props} />
    {children}
  </label>
);
const FixtureAvatar = ({ entrant, isTeam = true }) => {
  const image = entrant?.image || (entrant && isTeam ? teamAvatarSrc() : null);
  return <span className="flow-fixture-avatar" style={image ? undefined : avatarStyle(entrant?.name)}>{image ? <img src={image} alt="" className={entrant?.image_display_mode === "logo" || !entrant?.image ? "logo" : ""} /> : <Shield size={20} fill="currentColor" strokeWidth={1} aria-hidden="true" />}</span>;
};

function ScoreForm({ match, name, busy, act, revision, penaltyCount, adminMatch = false, onSaved }) {
  const [a, setA] = useState(match.score_a ?? (adminMatch ? "0" : "")),
    [b, setB] = useState(match.score_b ?? (adminMatch ? "0" : ""));
  const [pa, setPa] = useState(match.penalty_a ?? ""),
    [pb, setPb] = useState(match.penalty_b ?? "");
  const [reason, setReason] = useState(""),
    [rewind, setRewind] = useState(false),
    [error, setError] = useState("");
  const tied =
    match.bracket !== "group" &&
    a !== "" &&
    b !== "" &&
    Number(a) === Number(b);
  const correction = match.status === "completed";
  const submit = async (event) => {
    event.preventDefault();
    if (!whole(a) || !whole(b))
      return setError("Enter both final scores as whole numbers.");
    if (tied && (!whole(pa) || !whole(pb) || Number(pa) === Number(pb)))
      return setError("Enter the penalty result with a winning side.");
    if (correction && !reason.trim())
      return setError("Explain why this result is being corrected.");
    setError("");
    const result = await act(
      "score",
      {
        revision,
        match_id: match.id,
        result: {
          score_a: Number(a),
          score_b: Number(b),
          ...(tied ? { penalty_a: Number(pa), penalty_b: Number(pb) } : {}),
        },
        correct: correction,
        reason,
        rewind,
      },
      "Result saved. Standings and next games are updated.",
    );
    if (result) onSaved?.();
  };
  return (
    <form onSubmit={submit} aria-label="Game result" className={adminMatch ? "flow-admin-score-form" : undefined}>
      {!adminMatch && <h3>{correction ? "Correct result" : "Record result"}</h3>}
      {error && (
        <p role="alert" id="flow-score-error">
          {error}
        </p>
      )}
      <fieldset
        disabled={busy}
        aria-describedby={error ? "flow-score-error" : undefined}
      >
        <div className={adminMatch ? "flow-score-pad" : "flow-grid"}>
          <Field
            label={adminMatch ? name(match.participant_a) : `${name(match.participant_a)} score`}
            aria-label={`${name(match.participant_a)} score`}
            className={adminMatch && String(a) === "0" ? "flow-score-zero" : undefined}
            type="number"
            min="0"
            step="1"
            inputMode="numeric"
            required
            value={a}
            onChange={(e) => setA(e.target.value)}
          />
          {adminMatch && <span className="flow-score-dash" aria-hidden="true">—</span>}
          <Field
            label={adminMatch ? name(match.participant_b) : `${name(match.participant_b)} score`}
            aria-label={`${name(match.participant_b)} score`}
            className={adminMatch && String(b) === "0" ? "flow-score-zero" : undefined}
            type="number"
            min="0"
            step="1"
            inputMode="numeric"
            required
            value={b}
            onChange={(e) => setB(e.target.value)}
          />
        </div>
        {tied && (
          <>
            <p>
              Straight to penalties · {penaltyCount || 3} each, then sudden
              death if needed.
            </p>
            <div className="flow-grid">
              <Field
                label={`${name(match.participant_a)} penalties`}
                type="number"
                min="0"
                step="1"
                required
                value={pa}
                onChange={(e) => setPa(e.target.value)}
              />
              <Field
                label={`${name(match.participant_b)} penalties`}
                type="number"
                min="0"
                step="1"
                required
                value={pb}
                onChange={(e) => setPb(e.target.value)}
              />
            </div>
          </>
        )}
        {correction && (
          <>
            <Field
              label="Reason for correction"
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <Check
              checked={rewind}
              onChange={(e) => setRewind(e.target.checked)}
            >
              Rewind affected later games. Saved history is retained.
            </Check>
          </>
        )}
        <button type="submit">
          {busy
            ? "Saving…"
            : correction
              ? "Save corrected result"
              : adminMatch ? <><CircleCheck size={18} aria-hidden="true" /> Submit Result</> : "Save result"}
        </button>
      </fieldset>
    </form>
  );
}

function ScheduleForm({ match, busy, act, revision }) {
  const [court, setCourt] = useState(match.court || ""),
    [start, setStart] = useState(
      match.start_minute == null ? "" : clock(match.start_minute),
    );
  const [duration, setDuration] = useState(match.duration_minutes),
    [error, setError] = useState("");
  return (
    <form
      aria-label="Game details"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!whole(duration) || Number(duration) < 1)
          return setError("Enter playing time in whole minutes, at least 1.");
        if (start && !/^([01]\d|2[0-3]):[0-5]\d$/.test(start))
          return setError("Choose a valid time or leave it blank.");
        setError("");
        const [hours, minutes] = start.split(":").map(Number);
        await act(
          "schedule",
          {
            revision,
            match_id: match.id,
            changes: {
              court,
              start_minute: start ? hours * 60 + minutes : null,
              duration_minutes: Number(duration),
            },
          },
          "Game details saved. Planned times never prevent play.",
        );
      }}
    >
      <h3>Game details</h3>
      {error && (
        <p role="alert" id="flow-schedule-error">
          {error}
        </p>
      )}
      <fieldset
        disabled={busy}
        aria-describedby={error ? "flow-schedule-error" : undefined}
      >
        <Field
          label="Pitch / court / area"
          value={court}
          onChange={(e) => setCourt(e.target.value)}
        />
        <div className="flow-grid">
          <Field
            label="Planned time (optional)"
            type="time"
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
          <Field
            label="Playing minutes"
            type="number"
            min="1"
            step="1"
            required
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
          />
        </div>
        <button className="secondary" type="submit">
          Save game details
        </button>
      </fieldset>
    </form>
  );
}

function StageForm({ stage, state, busy, act, revision }) {
  const [seeds, setSeeds] = useState(stage.seeds || []),
    [reason, setReason] = useState(""),
    [rewind, setRewind] = useState(false),
    [error, setError] = useState("");
  const move = (id, direction) =>
    setSeeds((old) => {
      const next = [...old],
        i = next.indexOf(id),
        j = i + direction;
      if (j >= 0 && j < next.length) [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  const name = (id) =>
    state.entrants.find((e) => e.id === id)?.name || "Entrant";
  return (
    <form
      aria-label="Stage entrants"
      onSubmit={async (e) => {
        e.preventDefault();
        if (seeds.length !== stage.count)
          return setError(
            `Choose exactly ${stage.count} entrants for this stage.`,
          );
        if (!reason.trim())
          return setError("Explain why the entrants are changing.");
        setError("");
        await act(
          "seed",
          { revision, stage_id: stage.id, entrant_ids: seeds, reason, rewind },
          "Entrants updated. Fixtures have been recalculated.",
        );
      }}
    >
      <h3>
        {stage.label} · {seeds.length} of {stage.count} places
      </h3>
      {error && (
        <p role="alert" id="flow-seed-error">
          {error}
        </p>
      )}
      <fieldset
        disabled={busy}
        aria-describedby={error ? "flow-seed-error" : undefined}
      >
        <div className="flow-grid">
          {state.entrants.map((entrant) => (
            <Check
              key={entrant.id}
              checked={seeds.includes(entrant.id)}
              onChange={(e) =>
                setSeeds((old) =>
                  e.target.checked
                    ? [...old, entrant.id]
                    : old.filter((id) => id !== entrant.id),
                )
              }
            >
              {entrant.name}
            </Check>
          ))}
        </div>
        <h4>Seed order</h4>
        <ol className="flow-seeds">
          {seeds.map((id, index) => (
            <li key={id}>
              <span>{name(id)}</span>
              <div>
                <button
                  type="button"
                  className="secondary"
                  aria-label={`Move ${name(id)} up`}
                  disabled={!index}
                  onClick={() => move(id, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="secondary"
                  aria-label={`Move ${name(id)} down`}
                  disabled={index === seeds.length - 1}
                  onClick={() => move(id, 1)}
                >
                  ↓
                </button>
              </div>
            </li>
          ))}
        </ol>
        <Field
          label="Reason for changing entrants"
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <Check checked={rewind} onChange={(e) => setRewind(e.target.checked)}>
          Rewind this stage and later games. Saved history is retained.
        </Check>
        <button type="submit">Save stage entrants</button>
      </fieldset>
    </form>
  );
}

export default function ConfigurableTournament({ tournament }) {
  const { user, loading: authLoading } = useAuth();
  const owner = user?.id || "public";
  const [data, setData] = useState(null),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const [showProjectedQualifiers, setShowProjectedQualifiers] = useState(tournament.show_projected_qualifiers === true);
  const [projectionBusy, setProjectionBusy] = useState(false);
  const [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false),
    [times, setTimes] = useState(true);
  const [adminDashboard, setAdminDashboard] = useState(() => window.location.search.includes("admin=1"));
  const [tab, setTab] = useState(() => window.location.search.includes("admin=1") ? "Standings" : "Tree"),
    [selection, setSelection] = useState(null),
    [stageSelection, setStageSelection] = useState(null);
  const [visible, setVisible] = useState({});
  const [adminMatchId, setAdminMatchId] = useState(null);
  const [showCurrentSchedule, setShowCurrentSchedule] = useState(false);
  const [expandedStageId, setExpandedStageId] = useState(null);
  const formatScheduleRef = useRef(null);
  const [mobileScreen, setMobileScreen] = useState(() => window.matchMedia?.("(max-width: 650px)").matches ?? window.innerWidth <= 650);
  useEffect(() => {
    const media = window.matchMedia?.("(max-width: 650px)");
    if (!media) return undefined;
    const update = () => setMobileScreen(media.matches);
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, []);
  const [mobileOverview, setMobileOverview] = useState(() => window.innerWidth <= 650 && new URLSearchParams(window.location.search).get("overview") === "1");
  const [mobileStageId, setMobileStageId] = useState(() => new URLSearchParams(window.location.search).get("stage"));
  const [mobileTeamId, setMobileTeamId] = useState(() => window.innerWidth <= 650 ? new URLSearchParams(window.location.search).get("team") : null);
  const [mobileClubProfile, setMobileClubProfile] = useState(false);
  const [profileSeason, setProfileSeason] = useState("");
  const [profileRankMode, setProfileRankMode] = useState(0);
  const [profileStatModes, setProfileStatModes] = useState({});
  useEffect(() => {
    if (!mobileClubProfile) return undefined;
    let active = true;
    supabase.from("seasons").select("start,finish,display_name").then(({ data }) => {
      if (!active || !Array.isArray(data)) return;
      const now = Date.now();
      const seasons = data.map((season) => ({ ...season, startAt: Date.parse(season.start), finishAt: Date.parse(season.finish) }));
      const currentSeason = seasons.find((season) => now >= season.startAt && now < season.finishAt)
        || seasons.filter((season) => season.startAt > now).sort((a, b) => a.startAt - b.startAt)[0]
        || seasons.sort((a, b) => b.startAt - a.startAt)[0];
      setProfileSeason(currentSeason?.display_name || "");
    });
    return () => { active = false; };
  }, [mobileClubProfile]);
  const [testFormat, setTestFormat] = useState("custom");
  const [testCount, setTestCount] = useState(String(tournament.format_config?.blueprint?.entrant_count || 12));
  const runRef = useRef(null),
    busyRef = useRef(false),
    refreshRef = useRef(null),
    refreshPendingRef = useRef(false),
    requestRef = useRef(0),
    mounted = useRef(false),
    scopeRef = useRef(owner),
    simulationRef = useRef(!!tournament.format_config?.demo);
  scopeRef.current = owner;
  const current = data?.owner === owner ? data : null;
  const run = current?.run,
    state = run?.state,
    admin = !!user && !!current?.is_admin;
  const toggleProjectedQualifiers = async () => {
    if (!admin || projectionBusy) return;
    setProjectionBusy(true);
    const enabled = !showProjectedQualifiers;
    const { data: updated, error: updateError } = await supabase.from("tournaments")
      .update({ show_projected_qualifiers: enabled }).eq("id", tournament.id)
      .select("show_projected_qualifiers").single();
    setProjectionBusy(false);
    if (updateError) setError(updateError.message);
    else {
      setShowProjectedQualifiers(updated.show_projected_qualifiers === true);
      setNotice(`Projected qualifiers ${enabled ? "on" : "off"}.`);
    }
  };
  const hasSavedSimulation = (current?.runs || []).some((savedRun) => savedRun.is_simulation);
  const hasState = !!state;
  const name = (id) =>
    state?.entrants.find((e) => e.id === id)?.name || "To be decided";
  const displayEntrant = (id) => {
    const entrant = state?.entrants.find((item) => item.id === id);
    const enrollment = current?.enrollmentRows?.[id];
    return entrant && enrollment
      ? { ...entrant, image: enrollment.team_image || null, image_display_mode: teamDisplayMode(enrollment.team_image, enrollment.image_display_mode) }
      : entrant;
  };
  const avatarFor = (id) => {
    const entrant = displayEntrant(id);
    return entrant?.image || (entrant && tournament.game_type !== "1v1" ? teamAvatarSrc() : null);
  };
  const appLink = `snowsproam://tournament/${tournament.public_slug || tournament.id}`;
  const shareUrl = `https://snowsproam.com/tournaments/${encodeURIComponent(tournament.public_slug || tournament.id)}`;
  const shareTournament = async (kind) => {
    const url = kind === "app" ? appLink : shareUrl;
    const label = kind === "app" ? "App" : "Website";
    try {
      if (navigator.share) await navigator.share(kind === "app" ? { title: tournament.tournament_name, text: url } : { title: tournament.tournament_name, url });
      else {
        await navigator.clipboard.writeText(url);
        setNotice(`${label} tournament link copied.`);
      }
    } catch (shareError) {
      if (shareError.name === "AbortError") return;
      try {
        await navigator.clipboard.writeText(url);
        setNotice(`${label} tournament link copied.`);
      } catch {
        setError(`Could not share the ${label.toLowerCase()} link. Please try again.`);
      }
    }
  };
  const load = useCallback(
    async (args = {}) => {
      if (authLoading || busyRef.current) return;
      const request = ++requestRef.current;
      setLoading(true);
      try {
        let result;
        if (user) {
          try {
            result = await tournamentFlow(tournament.id, "read", {
              simulation: simulationRef.current,
              ...args,
            });
          } catch (readError) {
            if (
              !/session expired|sign in again|invalid jwt|jwt expired/i.test(
                readError.message,
              )
            )
              throw readError;
            result = await publicTournamentFlow(tournament.id);
          }
        } else result = await publicTournamentFlow(tournament.id);
        const { data: projectionSetting, error: projectionError } = await supabase.from("tournaments").select("show_projected_qualifiers").eq("id", tournament.id).single();
        if (!projectionError && request === requestRef.current) setShowProjectedQualifiers(projectionSetting?.show_projected_qualifiers === true);
        let enrollmentRows = {};
        let rosterError = "";
        if (user && !result?.run?.is_simulation && result?.run?.state?.entrants?.length) {
          const { data: rows, error: enrollmentError } = await supabase.from("tournament_enrolled")
            .select("id,team_id,team_name,team_image,image_display_mode,profile_id,profile_img,team_captain_id,members,max_members,spots_in_team")
            .eq("tournament_id", tournament.id)
            .in("id", result.run.state.entrants.map((entrant) => entrant.id));
          if (!enrollmentError) enrollmentRows = Object.fromEntries((rows || []).map((row) => [row.id, row]));
          else {
            try {
              const publicRows = await publicTournamentRosters(tournament.id);
              enrollmentRows = Object.fromEntries(publicRows.map((row) => [row.id, row]));
            } catch {
              rosterError = "Could not load team members. Refresh to try again.";
            }
          }
        } else if (!user && result?.run?.state?.entrants?.length) {
          try {
            const rows = await publicTournamentRosters(tournament.id);
            enrollmentRows = Object.fromEntries(rows.map((row) => [row.id, row]));
          } catch (rosterLoadError) {
            rosterError = rosterLoadError.message;
          }
        }
        if (!mounted.current || request !== requestRef.current) return;
        setData({ ...result, owner, enrollmentRows, rosterError });
        runRef.current = result.run;
        setError("");
      } catch (e) {
        if (mounted.current && request === requestRef.current)
          setError(e.message);
      } finally {
        if (mounted.current && request === requestRef.current)
          setLoading(false);
      }
    },
    [authLoading, owner, user, tournament.id],
  );
  useEffect(() => {
    mounted.current = true;
    simulationRef.current = !!tournament.format_config?.demo;
    runRef.current = null;
    setSelection(null);
    setStageSelection(null);
    setAdminDashboard(window.location.search.includes("admin=1"));
    setTab(window.location.search.includes("admin=1") ? "Standings" : "Tree");
    setData(null);
    load();
    return () => {
      mounted.current = false;
      // This counter invalidates async work; it is intentionally read at cleanup.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      requestRef.current++;
    };
  }, [load, tournament.format_config?.demo]);
  useEffect(() => {
    let stopped = false, refreshing = false, queued = false, debounce;
    const refresh = async () => {
      if (stopped || document.hidden) return;
      if (busyRef.current) { refreshPendingRef.current = true; return; }
      if (refreshing) { queued = true; return; }
      refreshing = true;
      try {
        // Follow the latest live run, but keep an explicitly viewed test run.
        await load(runRef.current?.is_simulation && runRef.current?.id ? { run_id: runRef.current.id } : {});
      } finally {
        refreshing = false;
        if (queued && !stopped) { queued = false; refresh(); }
      }
    };
    const scheduleRefresh = () => {
      if (stopped) return;
      clearTimeout(debounce);
      debounce = setTimeout(refresh, 80);
    };
    refreshRef.current = scheduleRefresh;
    const unsubscribe = subscribeTournamentFlow(tournament.id, scheduleRefresh, !!user);
    const timer = setInterval(refresh, 12000);
    window.addEventListener("online", refresh);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      stopped = true;
      refreshRef.current = null;
      clearTimeout(debounce);
      unsubscribe();
      clearInterval(timer);
      window.removeEventListener("online", refresh);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [load, tournament.id, user]);
  const act = async (action, args = {}, success = "") => {
    if (busyRef.current) return null;
    busyRef.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    requestRef.current++;
    const scope = owner;
    try {
      const result = await tournamentFlow(tournament.id, action, {
        run_id: runRef.current?.id,
        revision: runRef.current?.revision,
        ...args,
      });
      if (!mounted.current || scopeRef.current !== scope) return null;
      if (result.run) {
        simulationRef.current = !!result.run.is_simulation;
        runRef.current = result.run;
        setData((old) => ({
          ...old,
          ...result,
          owner: scope,
          runs: [
            result.run,
            ...(old?.runs || []).filter((r) => r.id !== result.run.id),
          ].slice(0, 20),
        }));
      }
      setNotice(result.notice || success);
      if (["score", "schedule", "seed"].includes(action)) {
        setSelection(null);
        setStageSelection(null);
      }
      return result;
    } catch (e) {
      if (mounted.current && scopeRef.current === scope) setError(e.message);
      return null;
    } finally {
      busyRef.current = false;
      if (mounted.current) {
        setBusy(false);
        setLoading(false);
        if (refreshPendingRef.current) {
          refreshPendingRef.current = false;
          refreshRef.current?.();
        }
      }
    }
  };
  const newSimulation = async () => {
    const count = Number(testCount);
    if (!Number.isInteger(count) || count < 2 || count > 30) return setError("Choose a whole number from 2 to 30 for the test entrants.");
    if (
      await act(
        "simulate",
        { run_id: undefined, simulation_format: testFormat, simulation_count: count },
        "New simulation ready. Earlier runs are retained.",
      )
    ) {
      setTab("Sim");
      setSelection(null);
      setStageSelection(null);
    }
  };
  const switchRun = (args) => {
    if (typeof args.simulation === "boolean")
      simulationRef.current = args.simulation;
    setSelection(null);
    setStageSelection(null);
    setNotice("");
    load(args);
  };
  const choose = (m) => setSelection({ match: m, revision: run.revision });
  const match =
    selection && state?.matches.find((m) => m.id === selection.match.id);
  const returnToFixtures = useCallback(() => {
    const id = selection?.match.id;
    setSelection(null);
    requestAnimationFrame(() =>
      document.getElementById(`flow-match-${id}`)?.focus(),
    );
  }, [selection]);
  const detailHeading = useRef(null);
  useEffect(() => {
    if (selection) detailHeading.current?.focus();
  }, [selection]);
  useEffect(() => {
    if (!selection) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !busy) returnToFixtures();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [selection, busy, returnToFixtures]);
  const playedCount = state?.matches.filter((game) => game.status === "completed").length || 0;
  const gameCount = state?.matches.filter((game) => !["bye", "skipped"].includes(game.status)).length || 0;
  const currentGame = (adminDashboard && adminMatchId && state?.matches.find((game) => game.id === adminMatchId)) || state?.matches.find((game) => game.status === "ready");
  const chooseAdminGame = (game) => {
    setAdminMatchId(game.id);
    setShowCurrentSchedule(false);
    document.getElementById("flow-current-game")?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };
  const entrantPlural = current?.tournament?.blueprint?.presentation?.entrant_plural || tournament.format_config?.blueprint?.presentation?.entrant_plural || state?.blueprint?.presentation?.entrant_plural || (tournament.game_type === "1v1" ? "players" : "teams");
  const goalDifferenceLabel = tournament.sport === "Football" ? "GD" : "diff";
  const mobileStage = state?.stages.find((stage) => stage.id === mobileStageId || stage.label?.toLowerCase() === mobileStageId?.toLowerCase()) || state?.stages.find((stage) => stage.status === "active") || state?.stages[0];
  const mobileMatches = (state?.matches || []).filter((game) => game.stage_id === mobileStage?.id && !["bye", "skipped"].includes(game.status));
  const mobileNextStage = state?.stages.find((stage) => stage.qualification?.source_stage_id === mobileStage?.id);
  const mobileQualifierIds = mobileNextStage ? visibleStageIds(state, mobileNextStage, tournament.format_config?.blueprint || state?.blueprint, showProjectedQualifiers) : [];
  const mobileKnockoutIds = mobileStage && !mobileStage.standings?.length ? visibleStageIds(state, mobileStage, tournament.format_config?.blueprint || state?.blueprint, showProjectedQualifiers) : [];
  const mobileTeamGames = (state?.matches || []).filter((game) => !["bye", "skipped"].includes(game.status) && (game.participant_a === mobileTeamId || game.participant_b === mobileTeamId));
  const mobileTeamDetails = mobileTeamId ? { ...state?.entrants.find((entrant) => entrant.id === mobileTeamId), ...current?.enrollmentRows?.[mobileTeamId] } : null;
  const mobileMembers = [mobileTeamDetails?.captain_profile, mobileTeamDetails?.owner_profile, ...(Array.isArray(mobileTeamDetails?.members) ? mobileTeamDetails.members : [])].filter(Boolean);
  const mobileRosterIds = [...new Set([mobileTeamDetails?.team_captain_id, mobileTeamDetails?.profile_id, ...mobileMembers.map((member) => member.profile_id || member.profileId || member.id)].filter(Boolean))];
  const mobileCapacity = Number(mobileTeamDetails?.max_members) > 0 ? Number(mobileTeamDetails.max_members) : mobileRosterIds.length + Math.max(0, Number(mobileTeamDetails?.spots_in_team) || 0);
  const mobileUpcomingGames = mobileTeamGames.filter((game) => game.status !== "completed");
  const mobileResults = mobileTeamGames.filter((game) => game.status === "completed").reverse();
  const memberName = (id) => { const member = mobileMembers.find((item) => (item.profile_id || item.profileId || item.id) === id); return [member?.name, member?.surname].filter(Boolean).join(" ").trim() || member?.username || "Player"; };
  const openTeamGames = (id) => {
    if (typeof window.matchMedia === "function" && window.matchMedia("(max-width: 650px)").matches) { setMobileClubProfile(false); setMobileTeamId(id); }
    else setTab("Matches");
  };
  const openOverviewTeam = (id) => { if (id) { setMobileOverview(false); setMobileClubProfile(false); setMobileTeamId(id); } };
  const overviewAvatar = (id) => {
    const image = avatarFor(id);
    const entrant = displayEntrant(id);
    return <span className="flow-mobile-overview-avatar" aria-hidden="true">{image && <img src={image} alt="" className={!entrant?.image || entrant?.image_display_mode === "logo" ? "logo" : ""} />}</span>;
  };
  const overviewGame = (game) => {
    const sides = [game.participant_a || (game.source_a?.type === "seed" ? mobileKnockoutIds[game.source_a.index] : null), game.participant_b || (game.source_b?.type === "seed" ? mobileKnockoutIds[game.source_b.index] : null)];
    return <article key={game.id} className="flow-mobile-overview-game"><div className="flow-mobile-overview-game-head"><button type="button" onClick={() => { setMobileOverview(false); choose(game); }} aria-label={`Open ${game.label} match`}>{game.label}</button><strong>{game.status === "completed" ? "Final" : game.status === "ready" ? "Ready to play" : "Upcoming"}</strong></div><small>{[game.start_minute == null ? null : clock(game.start_minute), game.court].filter(Boolean).join(" · ")}</small>{sides.map((id, index) => <button type="button" key={index} className="flow-mobile-overview-game-team" disabled={!id} onClick={() => openOverviewTeam(id)} aria-label={id ? `View ${name(id)} games` : "Team to be decided"}>{overviewAvatar(id)}<strong>{name(id)}</strong><b>{game.status === "completed" && game[`score_${index === 0 ? "a" : "b"}`] != null ? game[`score_${index === 0 ? "a" : "b"}`] : "–"}</b></button>)}</article>;
  };
  useEffect(() => {
    if (!mobileOverview && !mobileTeamId) return undefined;
    const selector = mobileClubProfile ? ".flow-mobile-profile" : mobileTeamId ? ".flow-mobile-team" : ".flow-mobile-overview";
    const dialog = document.querySelector(selector);
    dialog?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") { if (mobileClubProfile) setMobileClubProfile(false); else { setMobileOverview(false); setMobileTeamId(null); } }
      if (event.key !== "Tab") return;
      const controls = [...(dialog?.querySelectorAll("button:not(:disabled),a[href]") || [])];
      if (!controls.length) return;
      if (event.shiftKey && (document.activeElement === controls[0] || document.activeElement === dialog)) { event.preventDefault(); controls.at(-1).focus(); }
      else if (!event.shiftKey && document.activeElement === controls.at(-1)) { event.preventDefault(); controls[0].focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener("keydown", onKeyDown); };
  }, [mobileOverview, mobileTeamId, mobileClubProfile, hasState]);
  return (
    <main className={`flow-public${tab === "Tree" ? " flow-public-tree" : ""}${adminDashboard && admin ? " flow-public-admin" : ""}${mobileTeamId ? " flow-public-mobile-sheet" : mobileOverview ? " flow-public-mobile-overview" : ""}`}>
      <div className="flow-topbar">
        <a href="/tournaments/live" className="flow-brand"><img src={logo} alt="" /> <span>Snows <strong>ProAm</strong></span></a>
        <a href="/tournaments/live" className="flow-back">← All tournaments</a>
      </div>
      <div className="flow-mobile-app-header">
        <div className="flow-mobile-app-actions">
          <a href="/tournaments/live" aria-label="Close tournament"><IonChevronBack width={21} height={21} aria-hidden="true" /></a>
          <span />
          <button type="button" onClick={() => shareTournament("web")} aria-label="Share tournament invite"><IonShare width={18} height={18} aria-hidden="true" /></button>
          {state && <button type="button" onClick={() => setMobileOverview(true)} aria-label={`View ${mobileStage?.label || "tournament"} bracket overview`}><IonGitBranch width={18} height={18} aria-hidden="true" /></button>}
          {admin && state && !adminDashboard && <button type="button" onClick={() => { setAdminDashboard(true); setTab("Standings"); }} aria-label="Tournament settings"><IonSettings width={17} height={17} aria-hidden="true" /></button>}
        </div>
        <h1>{tournament.tournament_name}</h1>
        {state && <p>{state.entrants.length} {entrantPlural} · {gameCount} {gameCount === 1 ? "game" : "games"}</p>}
      </div>
      {adminDashboard && admin && <div className="flow-mobile-admin-header">
        <button type="button" className="flow-mobile-admin-back" aria-label={tab === "Standings" ? "Close tournament admin dashboard" : "Back to tournament overview"} onClick={() => { if (tab === "Standings") { setAdminDashboard(false); setTab("Tree"); } else setTab("Standings"); }}><IonChevronBack width={20} height={20} aria-hidden="true" /></button>
        <div className="flow-mobile-admin-copy"><h1>{tournament.tournament_name}</h1><p>{state?.blueprint?.name || tournament.sport || "Tournament"}</p></div>
        <button type="button" className="flow-mobile-admin-action flow-mobile-admin-reset" disabled={busy || !state?.stages?.length} onClick={() => { const first = state.stages[0]; if (window.confirm("Reset all tournament results and progression? Entrants and the schedule remain saved.")) act("rewind_stage", { stage_id: first.id, reason: "Organizer reset tournament progress" }, "Tournament progress reset."); }}><RotateCcw size={17} aria-hidden="true" /><span>Reset</span></button>
      </div>}
      <header className="flow-hero">
        {tournament.image || tournament.default_image ? <img className="flow-hero-art" src={tournament.image || tournament.default_image} alt="" /> : null}
        <div className="flow-hero-content">
          <div className="flow-hero-kicker">{run?.is_simulation ? "SIMULATION VIEW" : "TOURNAMENT"}<span className="flow-hero-kicker-divider">/</span>{tournament.sport || "Sport"}</div>
          <h1>{tournament.tournament_name}</h1>
          <p className="flow-hero-description">{tournament.description || "Follow every group, game and result as the tournament unfolds."}</p>
          <div className="flow-hero-meta">
            <span>{tournament.location || "Location to be confirmed"}</span>
            {tournament.date && !Number.isNaN(Date.parse(tournament.date)) && <span>{new Date(tournament.date).toLocaleDateString("en-IE", { day: "numeric", month: "long", year: "numeric" })}{tournament.start_time ? ` · ${tournament.start_time}` : ""}</span>}
          </div>
        </div>
        <div className="flow-hero-stats" aria-label="Tournament at a glance">
          <div><strong>{state?.entrants.length ?? "—"}</strong><span>{entrantPlural.toUpperCase()}</span></div>
          <div><strong>{gameCount || "—"}</strong><span>GAMES</span></div>
          <div><strong>{playedCount}</strong><span>RESULTS</span></div>
          <div><strong>{state?.stages.length ?? "—"}</strong><span>STAGES</span></div>
        </div>
        <div className="flow-hero-footer">
          <div className="flow-actions">
          <a href={appLink}>
            {tournament.format_config?.demo
              ? "Open tournament in app"
              : "Join / manage entry in app"}
          </a>
          <a className="secondary" href="https://linktr.ee/snowsproam" target="_blank" rel="noopener noreferrer">
            Get the app
          </a>
          <button className="secondary" onClick={() => shareTournament("app")}><Smartphone size={17} /> Share app</button>
          <button className="secondary" onClick={() => shareTournament("web")}><Globe2 size={17} /> Share website</button>
          {!user && !authLoading && (
            <a
              className="secondary"
              href={`/?auth=signin&redirect=${encodeURIComponent(`/tournaments/${tournament.public_slug || tournament.id}`)}`}
            >
              Sign in to manage tournament
            </a>
          )}
          <button
            className="secondary"
            disabled={busy || loading || authLoading}
            onClick={() => load(run?.id ? { run_id: run.id } : {})}
          >
            Refresh
          </button>
          </div>
          <p>Joining and creating tournaments happens in the Snows ProAm app.</p>
        </div>
        {tournament.format_config?.demo && (
          <p className="flow-note">
            Demo tournament · Test entrants only. No entry fees or rewards.
          </p>
        )}
      </header>
      {adminDashboard && admin && <div className="flow-admin-heading"><button type="button" className="secondary" onClick={() => { setAdminDashboard(false); setTab("Tree"); }}>← Tournament tree</button><div><small>TOURNAMENT DASHBOARD</small><h1>{tournament.tournament_name}</h1><p>{state?.blueprint?.name || tournament.sport || "Tournament fixtures"}</p></div><button type="button" className="secondary" disabled={busy || !state?.stages?.length} onClick={() => { const first = state.stages[0]; if (window.confirm("Reset all tournament results and progression? Entrants and the schedule remain saved.")) act("rewind_stage", { stage_id: first.id, reason: "Organizer reset tournament progress" }, "Tournament progress reset."); }}>Reset progress</button></div>}
      <div className="flow-content">
      {error && (
        <div role="alert" className="flow-error">
          <p>{error}</p>
          <button
            className="secondary"
            disabled={busy}
            onClick={() => load(run?.id ? { run_id: run.id } : {})}
          >
            Retry refresh
          </button>
        </div>
      )}
      {busy && <span role="status" className="flow-sr-only">Saving tournament</span>}
      {!busy && notice && <p role="status" className="flow-note">{notice}</p>}
      {authLoading || (loading && !current) ? (
        <div className="flow-loading-state"><span role="status" className="flow-sr-only">Loading tournament</span><span className="flow-loading-spinner" aria-hidden="true" /></div>
      ) : (
        <>
          {run?.is_simulation && (
            <p className="flow-note">
              <strong>SIMULATION</strong> · Test results only
            </p>
          )}
          {admin && tab !== "Sim" && hasSavedSimulation && (
            <div className="flow-run-switch">
              <span>{run?.is_simulation ? "Viewing a test run" : "Viewing the live tournament"}</span>
              <button className="secondary" disabled={busy || loading} onClick={() => { switchRun({ simulation: !run?.is_simulation }); setTab("Tree"); }}>
                {run?.is_simulation ? "View live tournament" : "View latest simulation"}
              </button>
            </div>
          )}
          <nav aria-label={adminDashboard && admin ? "Tournament dashboard sections" : "Tournament sections"} className="flow-actions">
            {(adminDashboard && admin
              ? [
                  { key: "Standings", label: "Overview", icon: mobileScreen ? IonGrid : LayoutGrid },
                  { key: "Settings", label: "Format", icon: mobileScreen ? IonTrophy : Trophy },
                  { key: "Matches", label: "Match", icon: mobileScreen ? IonPodium : Medal },
                  { key: "Queue", label: "Queue", icon: mobileScreen ? IonList : ListOrdered },
                  { key: "Sim", label: "Test", icon: mobileScreen ? IonFlask : FlaskConical },
                  { key: "Admins", label: "Admins", icon: mobileScreen ? IonPeople : UsersRound },
                ]
              : ["Tree", "Matches", "Standings"].map((key) => ({ key, label: key }))
            ).map((item) => (
              <button
                key={item.key}
                className={tab === item.key ? "" : "secondary"}
                aria-pressed={tab === item.key}
                disabled={busy}
                onClick={() => {
                  setTab(item.key);
                  setSelection(null);
                }}
              >
                {item.icon && <item.icon size={16} aria-hidden="true" />}<span className="flow-admin-tab-label">{item.label}</span>
              </button>
            ))}
          </nav>
          {!run && !error && (
            <section className="flow-card">
              <h2>
                {admin
                  ? "Your format is ready to prepare"
                  : "Fixtures are being prepared"}
              </h2>
              <p>
                {current?.tournament?.blueprint?.name ||
                  "The organizer will publish fixtures here. Member-only tournaments require sign-in."}
              </p>
              {admin && (
                <div className="flow-actions">
                  <button disabled={busy} onClick={newSimulation}>
                    Create simulation
                  </button>
                  {!tournament.format_config?.demo && (
                    <button
                      className="secondary"
                      disabled={busy}
                      onClick={() =>
                        act(
                          "start",
                          { run_id: undefined },
                          "Live tournament ready.",
                        )
                      }
                    >
                      Start with enrolled entrants
                    </button>
                  )}
                </div>
              )}
            </section>
          )}
          {state?.status === "completed" && (
            <section className="flow-note">
              <h2>Tournament complete</h2>
              <p>Winner: {name(state.winner_id)}</p>
            </section>
          )}
          {tab === "Tree" && state && (
            <TournamentNodeTree state={{ ...state, entrants: state.entrants.map((entrant) => displayEntrant(entrant.id)) }} blueprint={tournament.format_config?.blueprint || state.blueprint} title={tournament.tournament_name} entrantPlural={entrantPlural} isTeam={tournament.game_type !== "1v1"} showProjectedQualifiers={showProjectedQualifiers} onChoose={choose} onShowMatches={openTeamGames} onShareApp={() => shareTournament("app")} onShareWeb={() => shareTournament("web")} isAdmin={admin} onOpenSettings={() => { setAdminDashboard(true); setTab("Standings"); }} onStageChange={setMobileStageId} />
          )}
          {tab === "Matches" && state && !selection && (
            <>
              {currentGame && <section className={`flow-current-game${adminDashboard && admin ? " flow-current-game-admin" : ""}`} id="flow-current-game">
                <div className="flow-current-heading"><small>CURRENT GAME</small><h2>{currentGame.label}</h2><span>{mobileScreen ? <IonCalendar width={14} height={14} aria-hidden="true" /> : <CalendarDays size={14} aria-hidden="true" />}{clock(currentGame.start_minute)} · {currentGame.court || "Area flexible"}</span></div>
                {adminDashboard && admin && <p className="flow-match-step">{mobileScreen ? <IonFootball width={17} height={17} aria-hidden="true" /> : <Medal size={17} aria-hidden="true" />}{currentGame.status === "completed" ? "Final score" : currentGame.status === "ready" ? "Enter the final score for this game" : "This game is waiting for teams"}</p>}
                <div className="flow-current-versus"><div><FixtureAvatar entrant={displayEntrant(currentGame.participant_a)} isTeam={tournament.game_type !== "1v1"} /><strong>{name(currentGame.participant_a)}</strong></div><b>VS</b><div><FixtureAvatar entrant={displayEntrant(currentGame.participant_b)} isTeam={tournament.game_type !== "1v1"} /><strong>{name(currentGame.participant_b)}</strong></div></div>
                {adminDashboard && admin ? <>
                  {currentGame.status === "waiting" && <p>This fixture unlocks when its entrants are decided. An admin can assign entrants in Format.</p>}
                  {["ready", "completed"].includes(currentGame.status) && <ScoreForm key={`current-${currentGame.id}-${run.revision}`} match={currentGame} revision={run.revision} name={name} busy={busy} act={act} penaltyCount={state.blueprint?.penalties} adminMatch onSaved={() => setAdminMatchId(null)} />}
                  {currentGame.status === "completed" && <button type="button" className="secondary" disabled={busy} onClick={() => { if (window.confirm("Undo this result? This game and dependent later results return to unplayed. Earlier games stay saved.")) act("undo_result", { match_id: currentGame.id }, "Result undone. Progression updated.").then((result) => { if (result) setAdminMatchId(null); }); }}>Undo this result</button>}
                  <button type="button" className="secondary" onClick={() => document.getElementById("flow-fixtures")?.scrollIntoView?.({ behavior: "smooth", block: "start" })}>Choose another game</button>
                  {currentGame.status === "ready" && !run.is_simulation && !current?.tournament?.matchmaking_enabled && <p className="flow-match-help">Player matchmaking is off. An admin can enter the result here.</p>}
                  <button type="button" className="secondary" onClick={() => setShowCurrentSchedule((value) => !value)}>{showCurrentSchedule ? "Hide game details" : "Edit planned time and pitch"}</button>
                  {showCurrentSchedule && <ScheduleForm key={`current-schedule-${currentGame.id}-${run.revision}`} match={currentGame} revision={run.revision} busy={busy} act={act} />}
                </> : <button className="secondary" onClick={() => choose(currentGame)}>View game details</button>}
              </section>}
              <div className="flow-fixture-controls" id="flow-fixtures"><h2>{state.matches.filter((m) => m.status === "completed").length} results recorded</h2><Check checked={times} onChange={(e) => setTimes(e.target.checked)} aria-label="Show planned times and pitches">{mobileScreen && adminDashboard && admin ? "Times" : "Show planned times and pitches"}</Check></div>
              <p className="flow-fixture-guidance">{mobileScreen ? state.blueprint?.presentation?.schedule_note || "Planned times and playing areas are guidance. Results determine who advances." : "Planned times and pitches are guidance. Results determine who advances."}</p>
              {state.stages.map((stage) => {
                const matches = state.matches.filter(
                  (m) =>
                    m.stage_id === stage.id &&
                    !["bye", "skipped"].includes(m.status),
                );
                return (
                  <section key={stage.id} className="flow-fixture-stage">
                    <h2>{stage.label} <span>· {stage.status === "waiting" ? "Awaiting qualifiers" : stage.status}</span></h2>
                    <div className="flow-fixture-grid">
                      {matches.slice(0, visible[stage.id] || 40).map((m) => (
                        <button key={m.id} type="button" className={`flow-fixture-card flow-fixture-${m.status}`} id={`flow-match-${m.id}`} aria-label={`View ${m.label}: ${name(m.participant_a)} versus ${name(m.participant_b)}, ${m.status}`} onClick={() => adminDashboard && admin ? chooseAdminGame(m) : choose(m)}>
                          <span className="flow-fixture-card-top"><strong>{m.label}</strong><em>{m.status === "completed" ? mobileScreen ? "Played" : `${m.score_a}–${m.score_b} · Final` : m.status === "ready" ? "Ready" : "Waiting"}</em></span>
                          {[m.participant_a, m.participant_b].map((id, index) => <span className="flow-fixture-team-row" key={`${m.id}-${index}`}><FixtureAvatar entrant={displayEntrant(id)} isTeam={tournament.game_type !== "1v1"} /><strong>{name(id)}</strong><b>{m.status === "completed" ? index === 0 ? m.score_a : m.score_b : index === 0 ? "vs" : ""}</b></span>)}
                          {times && <span className="flow-fixture-schedule">{mobileScreen && <IonTime width={14} height={14} aria-hidden="true" />}<span>{clock(m.start_minute)}</span><i>·</i><span>{m.court || "Area flexible"}</span><i>·</i><span>{m.duration_minutes} min</span></span>}
                          {m.status === "completed" && m.penalty_a != null && <span className="flow-fixture-penalties">Penalties {m.penalty_a}–{m.penalty_b}</span>}
                          {(mobileScreen ? m.status !== "completed" : true) && <span className="flow-fixture-card-foot">{m.status === "ready" ? admin ? "Ready · record score or open game" : "Ready · open game" : m.status === "completed" ? "Final result" : "Waiting for earlier results"}</span>}
                        </button>
                      ))}
                    </div>
                    {!matches.length && (
                      <p>Fixtures will appear when entrants are ready.</p>
                    )}
                    {matches.length > (visible[stage.id] || 40) && (
                      <button
                        className="secondary"
                        onClick={() =>
                          setVisible((old) => ({
                            ...old,
                            [stage.id]: (old[stage.id] || 40) + 40,
                          }))
                        }
                      >
                        Show more {stage.label} games
                      </button>
                    )}
                  </section>
                );
              })}
            </>
          )}
          {tab === "Queue" && state && admin && (
            <section className="flow-admin-queue">
              <div className="flow-queue-hero">
              <span className="flow-queue-hero-icon" aria-hidden="true"><ListOrdered size={20} /></span>
              <h2>{state.matches.filter((game) => game.status === "ready").length} games ready</h2>
              <p>{current?.tournament?.matchmaking_enabled ? "Each side joins matchmaking from the app. Open a game to record its score or edit its schedule." : "Player matchmaking is off. Open a game to record its score or edit its schedule."}</p>
              </div>
              <button className="secondary" onClick={() => setTab("Matches")}>View all games and results</button>
              <div className="flow-grid">
                {state.matches.filter((game) => game.status === "ready").map((game) => (
                  <button key={game.id} type="button" className="flow-queue-game" onClick={() => { setTab("Matches"); choose(game); }} aria-label={`Open ${game.label}, ${name(game.participant_a)} versus ${name(game.participant_b)}`}>
                    <span className="flow-queue-head"><span>{game.label}</span><b>{clock(game.start_minute)}</b></span>
                    <strong className="flow-queue-desktop-teams">{name(game.participant_a)} <b>VS</b> {name(game.participant_b)}</strong>
                    <span className="flow-queue-mobile-teams"><span><FixtureAvatar entrant={displayEntrant(game.participant_a)} isTeam={tournament.game_type !== "1v1"} /><strong>{name(game.participant_a)}</strong><b>VS</b></span><span><FixtureAvatar entrant={displayEntrant(game.participant_b)} isTeam={tournament.game_type !== "1v1"} /><strong>{name(game.participant_b)}</strong></span></span>
                    <small>{game.court || "Area flexible"} · {game.duration_minutes} min{current?.tournament?.matchmaking_enabled && !run.is_simulation ? ` · ${(current.queue_participants?.[game.id] || []).length} of 2 joined matchmaking` : ""}</small>
                    <span className="flow-queue-mobile-open">Open game <ChevronLeft size={14} aria-hidden="true" /></span>
                  </button>
                ))}
              </div>
              {!state.matches.some((game) => game.status === "ready") && <p>{state.status === "completed" ? "All games finished. The queue is clear." : "No games ready yet. Complete earlier games to unlock the next fixtures."}</p>}
            </section>
          )}
          {match && (
            <div className="flow-modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) returnToFixtures(); }}>
            <section className="flow-card flow-game-dialog" role="dialog" aria-modal="true" aria-label={`${match.label} details`}>
              <div className="flow-dialog-top"><span className="flow-section-kicker">GAME DETAILS</span><button className="secondary" disabled={busy} onClick={returnToFixtures}>Close</button></div>
              <h2 ref={detailHeading} tabIndex={-1}>
                {match.label}
              </h2>
              <div className="flow-dialog-versus"><span>{name(match.participant_a)}</span><b>VS</b><span>{name(match.participant_b)}</span></div>
              <div className="flow-dialog-meta"><span>{clock(match.start_minute)}</span><span>{match.court || "Area flexible"}</span><span>{match.duration_minutes} min game</span></div>
              {match.status === "completed" && (
                <p className="flow-dialog-result">
                  Result: {match.score_a}–{match.score_b}
                  {match.penalty_a != null
                    ? ` · penalties ${match.penalty_a}–${match.penalty_b}`
                    : ""}
                </p>
              )}
              {admin && (
                <>
                  {selection.revision !== run.revision && (
                    <p className="flow-note">
                      This tournament has changed since you opened this game.
                      Your input is preserved.{" "}
                      <button
                        className="secondary"
                        disabled={busy}
                        onClick={() => choose(match)}
                      >
                        Load latest game details
                      </button>
                    </p>
                  )}
                  {["ready", "completed"].includes(match.status) && (
                    <ScoreForm
                      key={`score-${match.id}-${selection.revision}`}
                      match={selection.match}
                      revision={selection.revision}
                      name={name}
                      busy={busy}
                      act={act}
                      penaltyCount={state.blueprint?.penalties}
                    />
                  )}
                  <ScheduleForm
                    key={`schedule-${match.id}-${selection.revision}`}
                    match={selection.match}
                    revision={selection.revision}
                    busy={busy}
                    act={act}
                  />
                </>
              )}
              {!run.is_simulation &&
                state.entrants.some(
                  (e) =>
                    [match.participant_a, match.participant_b].includes(e.id) &&
                    (e.captain_id || e.profile_id) === current?.actor_id,
                ) &&
                match.status === "ready" && (
                  <a href={appLink}>Open matchmaking in app</a>
                )}
            </section>
            </div>
          )}
          {tab === "Standings" && state && (
            <section className="flow-standings-screen">
              {adminDashboard && admin && <><div className="flow-overview-banner"><span className="flow-overview-banner-icon"><Flag size={22} aria-hidden="true" /></span><small>TOURNAMENT OVERVIEW</small><h2>Run your tournament</h2><p>{state.blueprint?.name || tournament.tournament_name}</p></div><div className="flow-overview-metrics"><div><strong>{state.entrants.length}</strong><span>{entrantPlural.toUpperCase()}</span></div><div><strong>{state.matches.filter((game) => game.status === "completed").length}</strong><span>GAMES</span></div><div><strong>{state.matches.filter((game) => game.status === "ready").length}</strong><span>WAITING</span></div></div><h2>Tournament {entrantPlural} · {state.entrants.length}</h2><div className="flow-overview-clubs">{state.entrants.map((entrant) => { const row = state.stages.flatMap((stage) => stage.standings || []).flatMap((group) => group.rows || []).find((item) => item.id === entrant.id); return <div key={entrant.id}><span className="flow-overview-avatar" style={avatarFor(entrant.id) ? undefined : avatarStyle(entrant.name)}>{avatarFor(entrant.id) ? <img src={avatarFor(entrant.id)} alt="" className={!displayEntrant(entrant.id)?.image || displayEntrant(entrant.id)?.image_display_mode === "logo" ? "logo" : ""} /> : <Shield size={20} fill="currentColor" strokeWidth={1} aria-hidden="true" />}</span><span><strong>{entrant.name}</strong><small>{row?.played || 0} played · {row?.goal_difference > 0 ? "+" : ""}{row?.goal_difference || 0} {goalDifferenceLabel}</small></span><b>{row?.points || 0} pts</b></div>; })}</div></>}
              <div className="flow-standings-heading"><div><small>GROUP TABLES</small><h2>Standings</h2></div><span>{state.stages.flatMap((stage) => stage.standings || []).length} groups</span></div>
              <p className="flow-standings-rules">
                {state.blueprint?.rules ||
                  state.rules ||
                  "Points, goal difference, goals scored, wins, then seed order."}
              </p>
              {state.stages
                .filter((s) => s.standings?.length)
                .map((stage) => (
                  <section key={stage.id} className="flow-standings-stage">
                    <h3>{stage.label}</h3>
                    <div className="flow-standings-groups">{stage.standings.map((group) => (
                      <article key={group.id} className="flow-standings-group">
                        <div className="flow-standings-group-title"><div><small>{stage.label}</small><h4>{group.name}</h4></div><span>{group.rows.length} {entrantPlural}</span></div>
                        <div
                          className="flow-table flow-standings-table"
                          tabIndex={0}
                          role="region"
                          aria-label={`${group.name} standings`}
                        >
                          <table>
                            <thead>
                              <tr>
                                <th scope="col">{entrantPlural === "players" ? "Player" : entrantPlural === "teams" ? "Team" : "Entrant"}</th>
                                <th scope="col">P</th>
                                <th scope="col">{goalDifferenceLabel}</th>
                                <th scope="col">PTS</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.rows.map((row) => (
                                <tr key={row.id}>
                                  <th scope="row"><span className="flow-standing-club"><span className="flow-standing-rank">{row.rank}</span><FixtureAvatar entrant={displayEntrant(row.id)} isTeam={tournament.game_type !== "1v1"} /><span>{name(row.id)}</span></span></th>
                                  <td>{row.played}</td>
                                  <td>{row.goal_difference > 0 ? "+" : ""}{row.goal_difference}</td>
                                  <td className="flow-standing-points">{row.points}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {!group.rows.length && <p>Waiting for entrants.</p>}
                      </article>
                    ))}</div>
                  </section>
                ))}
              {!state.stages.some((s) => s.standings?.length) && (
                <p>
                  This format advances match winners. See Matches for bracket
                  rounds.
                </p>
              )}
            </section>
          )}
          {tab === "Settings" && state && admin && (
            <section className="flow-settings-screen">
              <div className="flow-format-mobile-only flow-format-mobile-heading"><h2>Tournament Format</h2><button type="button" onClick={() => formatScheduleRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}>View schedule <span aria-hidden="true">⌄</span></button></div>
              <div className="flow-format-summary"><span className="flow-format-mobile-only flow-format-mobile-icon"><Trophy size={20} aria-hidden="true" /></span><small>CUSTOM TOURNAMENT FORMAT</small><h2>{state.blueprint?.name || tournament.tournament_name}</h2><p>{state.blueprint?.rules || "Progression follows the configured stages below."}</p><span>{state.entrants.length} {mobileScreen ? "clubs" : state.entrants.length === 1 ? "entrant" : "entrants"} · {state.matches.filter((game) => !["bye", "skipped"].includes(game.status)).length} games · {state.stages.length} stages</span></div>
              {!run.is_simulation && <div className="flow-matchmaking-setting"><div><strong>Matchmaking</strong><p>On: both sides join a ready game through Matchmaking. Off: admins record scores in Match.</p></div><button type="button" className={current?.tournament?.matchmaking_enabled ? "" : "secondary"} disabled={busy} aria-label="Player matchmaking" aria-pressed={!!current?.tournament?.matchmaking_enabled} onClick={async () => { const enabled = !current?.tournament?.matchmaking_enabled; const response = await act("set_matchmaking_enabled", { enabled }); if (response) setData((old) => ({ ...old, tournament: { ...old.tournament, matchmaking_enabled: response.matchmaking_enabled } })); }}><span className="flow-matchmaking-knob" aria-hidden="true" /><span className="flow-matchmaking-text">{current?.tournament?.matchmaking_enabled ? "On" : "Off"}</span></button></div>}
              <div className="flow-matchmaking-setting"><div><strong>Projected qualifiers</strong><p>Show likely teams in later stages before qualification is confirmed.</p></div><button type="button" className={showProjectedQualifiers ? "" : "secondary"} disabled={projectionBusy} aria-label="Projected qualifiers" aria-pressed={showProjectedQualifiers} onClick={toggleProjectedQualifiers}><span className="flow-matchmaking-knob" aria-hidden="true" /><span className="flow-matchmaking-text">{showProjectedQualifiers ? "On" : "Off"}</span></button></div>
              <h2>Stage progression</h2>
              <p>
                Choose a stage, select entrants and arrange their seed order.
                Groups and fixtures follow that order.
              </p>
              <div className="flow-stage-progress-grid">{state.stages.map((stage, index) => { const spec = state.blueprint?.stages?.[index] || stage; const laterResults = state.matches.filter((game) => state.stages.findIndex((item) => item.id === game.stage_id) >= index && game.status === "completed").length; const previewIds = visibleStageIds(state, stage, tournament.format_config?.blueprint || state.blueprint, showProjectedQualifiers); const shownIds = expandedStageId === stage.id ? previewIds : previewIds.slice(0, 4); return <article key={stage.id}><div><span>{index + 1}</span><strong>{stage.label}</strong><small>{stage.qualification?.overridden ? "Admin selected" : stage.status === "active" ? "In play" : stage.status === "completed" ? "Complete" : "Waiting"}</small></div><p>{qualificationLabel(spec)}</p><p>{stage.seeds?.length || 0}/{stage.count || 0} through</p>{spec.entry?.method === "ranks" && <p className="flow-format-mobile-only">Qualifiers move here automatically when the previous stage is complete. Admins can adjust the teams below.</p>}{shownIds.length > 0 && <div className="flow-format-mobile-only flow-stage-team-list">{shownIds.map((id) => { const entrant = state.entrants.find((item) => item.id === id); return <div key={id}><span style={avatarFor(id) ? undefined : avatarStyle(entrant?.name)}>{avatarFor(id) ? <img src={avatarFor(id)} alt="" className={!displayEntrant(id)?.image || displayEntrant(id)?.image_display_mode === "logo" ? "logo" : ""} /> : <Shield size={18} fill="currentColor" strokeWidth={1} aria-hidden="true" />}</span><strong>{entrant?.name || "Entrant"}</strong></div>; })}</div>}{previewIds.length > 4 && <button type="button" className="flow-format-mobile-only flow-stage-show-all" onClick={() => setExpandedStageId((currentId) => currentId === stage.id ? null : stage.id)}>{expandedStageId === stage.id ? "Show fewer" : `Show all ${previewIds.length}`}</button>}<div className="flow-actions"><button className="secondary" onClick={() => setStageSelection({ stage, revision: run.revision })}>{mobileScreen ? `Edit ${stage.label} clubs` : "Edit teams"}</button>{laterResults > 0 && <button className="secondary" disabled={busy} onClick={() => { if (window.confirm(`Reset ${stage.label} and all later stage results?`)) act("rewind_stage", { stage_id: stage.id }, `${stage.label} results reset.`); }}>Reset results</button>}</div><p className="flow-format-mobile-only flow-stage-action-help">Choose which teams play here and their order.{laterResults > 0 ? " Reset results first to change them; this also clears later stage results." : ""}</p>{mobileScreen && stageSelection?.stage.id === stage.id && <div className="flow-format-mobile-only flow-mobile-stage-editor"><StageForm key={`${stage.id}-${stageSelection.revision}`} stage={stage} revision={stageSelection.revision} state={state} busy={busy} act={act} /></div>}</article>; })}</div>
              <label className="flow-field flow-desktop-stage-picker">
                Stage
                <select
                  value={stageSelection?.stage.id || ""}
                  disabled={busy}
                  onChange={(e) => {
                    const stage = state.stages.find(
                      (s) => s.id === e.target.value,
                    );
                    setStageSelection(
                      stage ? { stage, revision: run.revision } : null,
                    );
                  }}
                >
                  <option value="">Choose a stage</option>
                  {state.stages.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label} · {s.count} places
                    </option>
                  ))}
                </select>
              </label>
              {!mobileScreen && stageSelection && (
                <article className="flow-desktop-stage-editor">
                  {stageSelection.revision !== run.revision && (
                    <p className="flow-note">
                      This tournament has changed.{" "}
                      <button
                        className="secondary"
                        disabled={busy}
                        onClick={() =>
                          setStageSelection({
                            stage: state.stages.find(
                              (s) => s.id === stageSelection.stage.id,
                            ),
                            revision: run.revision,
                          })
                        }
                      >
                        Load latest stage entrants
                      </button>
                    </p>
                  )}
                  <StageForm
                    key={`${stageSelection.stage.id}-${stageSelection.revision}`}
                    stage={stageSelection.stage}
                    revision={stageSelection.revision}
                    state={state}
                    busy={busy}
                    act={act}
                  />
                </article>
              )}
              <div className="flow-desktop-schedule-notes"><h3>Schedule notes</h3>
              {state.warnings?.length ? (
                <ul>
                  {state.warnings.map((warning, i) => (
                    <li key={i}>{warning}</li>
                  ))}
                </ul>
              ) : (
                <p>
                  No overlapping planned games found. Timing remains optional.
                </p>
              )}
              <button
                className="secondary"
                disabled={busy}
                onClick={() =>
                  act(
                    "refresh",
                    {},
                    "Tournament refreshed using the server engine.",
                  )
                }
              >
                Refresh from server rules
              </button></div>
              <h2 ref={formatScheduleRef}>Schedule</h2>
              <div className="flow-format-mobile-only flow-format-schedule-summary"><span><IonCalendarNumber width={20} height={20} aria-hidden="true" /></span><div><small>GAME TIMETABLE</small><strong>{state.matches.length} games · {state.stages.length} stages</strong><p>Planned times and playing areas are guidance. Open a game to edit its schedule.</p></div></div>
              <p className="flow-desktop-schedule-note">Planned times and pitches are guidance. Open a game to edit them.</p>
              {state.stages.map((stage) => <div key={`schedule-${stage.id}`} className="flow-schedule-stage"><h3>{stage.label}</h3>{state.matches.filter((game) => game.stage_id === stage.id && !["bye", "skipped"].includes(game.status)).map((game) => <button type="button" key={game.id} className="flow-schedule-game" onClick={() => { setTab("Matches"); choose(game); }}><span>{clock(game.start_minute)}<small>{game.court || "Area"}</small></span><strong>{game.label}<small>{name(game.participant_a)} · {name(game.participant_b)}</small></strong><span>{game.duration_minutes} min</span></button>)}</div>)}
            </section>
          )}
          {tab === "Settings" && admin && current?.tournament?.blueprint && (
            <section>
              <h2>Format rules</h2>
              <p>{current.tournament.blueprint.name || tournament.tournament_name}</p>
              <p>{current.tournament.blueprint.rules || "Progression follows the configured stages below."}</p>
              <p>{current.tournament.blueprint.entrant_count} entrants · {current.tournament.blueprint.penalties || 3} initial penalties in knockout games</p>
              <div className="flow-grid">
                {(current.tournament.blueprint.stages || []).map((stage) => (
                  <article key={stage.id}>
                    <h3>{stage.label || stage.id}</h3>
                    <p>{stage.type.replaceAll("_", " ")} · {stage.count} entrants</p>
                    {stage.type === "groups" && <p>{stage.group_sizes ? stage.group_sizes.join(" + ") : `${stage.count / stage.group_size} groups of ${stage.group_size}`}</p>}
                    {stage.entry && <p>Qualifies from {stage.entry.from} by {stage.entry.method}{stage.entry.ranks?.length ? ` · places ${stage.entry.ranks.join(", ")}` : ""}{stage.entry.wildcard ? ` · ${stage.entry.wildcard.count} wildcard places` : ""}</p>}
                  </article>
                ))}
              </div>
              <p className="flow-note">To change these rules, contact Snows ProAm. Existing results keep their current format.</p>
            </section>
          )}
          {tab === "Settings" && admin && !current?.tournament?.blueprint && (
            <p className="flow-note">
              This tournament has no format yet. Ask the organizer to prepare
              its blueprint.
            </p>
          )}
          {tab === "Sim" && admin && (
            <section className={`flow-admin-test${run?.is_simulation ? " flow-test-running" : ""}`}>
              <h2>Test entrants</h2>
              <div className="flow-test-mobile-intro"><span aria-hidden="true"><FlaskConical size={22} /></span><strong>{run?.is_simulation ? "Test preview" : "Preview safely"}</strong><p>{run?.is_simulation ? `Temporary test ${entrantPlural} are shown here. Your real ${entrantPlural} and scores stay exactly as they are.` : `Try any included mode with 2–30 test ${entrantPlural}. The preview uses temporary entrants and keeps the live tournament untouched.`}</p></div>
              {hasSavedSimulation && <button
                type="button"
                className="flow-test-mobile-return secondary"
                disabled={busy || loading}
                onClick={() => switchRun({ simulation: !run?.is_simulation })}
              >
                {run?.is_simulation ? "Return to live tournament" : "Open latest test preview"}
              </button>}
              <p>
                Try any included mode with 2–30 temporary entrants. Live entrants and results stay saved.
              </p>
              <h3 className="flow-test-mobile-label">Test format</h3>
              <div className="flow-test-formats" role="group" aria-label="Test format">{[
                ["custom", "My custom format", "Your configured stages"],
                ["single_elimination", "Single elimination", "Free · Basic · Pro"],
                ["double_elimination", "Double elimination", "Pro"],
                ["round_robin", "Round robin", "Standard · Pro"],
                ["double_round_robin", "Double round robin", "Pro"],
                ["group_to_knockout", "Groups into knockout", "Standard · Pro"],
              ].map(([key, label, detail]) => <button type="button" key={key} className={testFormat === key ? "" : "secondary"} aria-pressed={testFormat === key} onClick={() => setTestFormat(key)}><strong>{label}</strong><small>{detail}</small></button>)}</div>
              <div className="flow-test-counts" role="group" aria-label="Test entrant count">{[2,4,8,12,16,24,30].map((count) => <button type="button" key={count} className={testCount === String(count) ? "" : "secondary"} aria-pressed={testCount === String(count)} onClick={() => setTestCount(String(count))}>{count}</button>)}</div>
              <Field label="Test entrants (2–30)" type="number" min="2" max="30" value={testCount} onChange={(event) => setTestCount(event.target.value)} />
              <div className="flow-actions">
                <button disabled={busy || !Number.isInteger(Number(testCount)) || Number(testCount) < 2 || Number(testCount) > 30} onClick={newSimulation}>
                  {mobileScreen ? "Start test preview" : "Create a new simulation"}
                </button>
                {run?.is_simulation && (
                  <>
                    <button className="secondary" disabled={busy} onClick={() => { if (window.confirm("Clear this simulation and its test games? Live results stay saved.")) act("clear_simulation").then((response) => { if (response?.cleared) switchRun({ simulation: false }); }); }}>Clear this simulation</button>
                  </>
                )}
              </div>
              {run?.is_simulation && <><h3>Temporary entrants · {state.entrants.length}</h3><div className="flow-test-entrants">{state.entrants.map((entrant) => <span key={entrant.id}>{entrant.name}</span>)}</div></>}
            </section>
          )}
          {tab === "Admins" && admin && <TournamentAdminAccess tournamentId={tournament.id} busy={busy} onNotice={setNotice} onEdit={() => { window.location.assign(`/tournaments/live?edit=${encodeURIComponent(tournament.id)}`); }} />}
        </>
      )}
      </div>
      {mobileOverview && state && <div className="flow-mobile-overview-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setMobileOverview(false); }}>
        <section className="flow-mobile-overview" role="dialog" aria-modal="true" aria-labelledby="flow-mobile-overview-title" tabIndex={-1}>
          <header><div><h2 id="flow-mobile-overview-title">{mobileStage?.label || "Tournament"} bracket</h2><p>{mobileMatches.length} {mobileMatches.length === 1 ? "game" : "games"} · {mobileStage?.status === "active" ? "In play" : mobileStage?.status === "completed" ? "Complete" : "Upcoming"}</p></div><button type="button" aria-label="Close bracket overview" onClick={() => setMobileOverview(false)}><X size={20} /></button></header>
          <div className="flow-mobile-overview-content">
            {mobileNextStage && <section><h3>{mobileNextStage.label} · {mobileNextStage.count || mobileQualifierIds.length} qualifying spots</h3><p>{mobileNextStage.seeds?.length ? "Confirmed qualifiers" : mobileQualifierIds.length ? "Projected qualifiers" : "Places fill after Group Stages"}</p>{Array.from({ length: Math.max(Number(mobileNextStage.count || 0), mobileQualifierIds.length) }, (_, index) => <button type="button" className="flow-mobile-overview-row" key={`qualifier-${index}`} disabled={!mobileQualifierIds[index]} onClick={() => openOverviewTeam(mobileQualifierIds[index])}><b>{index + 1}</b>{overviewAvatar(mobileQualifierIds[index])}<strong>{mobileQualifierIds[index] ? name(mobileQualifierIds[index]) : "To be decided"}</strong></button>)}</section>}
            {(mobileStage?.standings || []).map((group) => {
              const groupMatches = mobileMatches.filter((game) => game.group_id === group.id);
              const rounds = [...new Set(groupMatches.map((game) => Number(game.round || 1)))].sort((a, b) => a - b);
              return <section key={group.id}><h3>{group.name}</h3><p>{group.rows?.length || 0} teams · {groupMatches.length} games</p>{(group.rows || []).map((row) => <button type="button" className="flow-mobile-overview-row" key={row.id} onClick={() => openOverviewTeam(row.id)} aria-label={`View ${name(row.id)} games`}><b>{row.rank}</b>{overviewAvatar(row.id)}<strong>{name(row.id)}</strong><span>{row.points || 0} pts</span><IonCalendar width={17} height={17} color="#0C69C8" aria-hidden="true" /></button>)}{rounds.map((round) => <div className="flow-mobile-overview-round" key={round}><h4>Round {round}</h4>{groupMatches.filter((game) => Number(game.round || 1) === round).map(overviewGame)}</div>)}</section>;
            })}
            {!mobileStage?.standings?.length && mobileMatches.length > 0 && <><section className="flow-mobile-overview-seeds"><h3>{mobileStage?.label} · {Number(mobileStage?.count || mobileKnockoutIds.length)} entrants</h3>{Array.from({ length: Math.max(Number(mobileStage?.count || 0), mobileKnockoutIds.length) }, (_, index) => <button type="button" className="flow-mobile-overview-row" key={`seed-${index}`} disabled={!mobileKnockoutIds[index]} onClick={() => openOverviewTeam(mobileKnockoutIds[index])}><b>{index + 1}</b>{overviewAvatar(mobileKnockoutIds[index])}<strong>{mobileKnockoutIds[index] ? name(mobileKnockoutIds[index]) : "To be decided"}</strong></button>)}</section>{[...new Set(mobileMatches.map((game) => `${game.bracket || "winners"}:${game.round || 1}`))].map((key) => { const games = mobileMatches.filter((game) => `${game.bracket || "winners"}:${game.round || 1}` === key); const [bracket, round] = key.split(":"); const label = String(games[0]?.label || "").replace(/\s*\d+$/, "").trim(); const title = /quarter|\bqf\b/i.test(label) ? "Quarterfinals" : /semi|\bsf\b/i.test(label) ? "Semifinals" : /final/i.test(label) ? "Final" : label || `Round ${round}`; return <section key={key} className="flow-mobile-overview-fixtures"><h3>{bracket === "winners" ? title : `${bracket} · ${title}`}</h3><p>{games.length} {games.length === 1 ? "game" : "games"}</p>{games.map(overviewGame)}</section>; })}</>}
            {!mobileMatches.length && <p>Fixtures are being prepared.</p>}
          </div>
        </section>
      </div>}
      {mobileTeamId && state && !mobileClubProfile && <section className="flow-mobile-team" role="dialog" aria-modal="true" aria-labelledby="flow-mobile-team-title" tabIndex={-1}>
        <header><button type="button" aria-label="Back to tournament tree" onClick={() => setMobileTeamId(null)}><IonChevronBack aria-hidden="true" /></button><div><h2 id="flow-mobile-team-title">Tournament games</h2><p>{tournament.tournament_name}</p></div></header>
        <div className="flow-mobile-team-content">
          <div className="flow-mobile-team-name"><span className={`flow-mobile-team-logo${mobileTeamDetails?.image_display_mode === "logo" || !(mobileTeamDetails?.team_image || mobileTeamDetails?.image) ? " logo" : ""}`}><img src={teamAvatarSrc(mobileTeamDetails?.team_image || mobileTeamDetails?.image)} alt="" /></span><strong>{name(mobileTeamId)}</strong>{mobileTeamDetails?.profile_id && tournament.game_type === "1v1" ? <a href={`/profile/${mobileTeamDetails.profile_id}`} aria-label={`View ${name(mobileTeamId)} profile`}><IonPeople aria-hidden="true" /></a> : <button type="button" aria-label={`View ${name(mobileTeamId)} club profile`} onClick={() => setMobileClubProfile(true)}><IonPeople aria-hidden="true" /></button>}</div>
          {mobileCapacity > 0 && tournament.game_type !== "1v1" && <div className="flow-mobile-team-spots"><strong>Team spots <span>{mobileRosterIds.length}/{mobileCapacity}</span></strong><small>Swipe to see all ›</small><div>{mobileRosterIds.map((id) => <a href={`/profile/${id}`} key={id} aria-label={`View ${memberName(id)} profile`}><span>{mobileMembers.find((item) => (item.profile_id || item.profileId || item.id) === id)?.profile_img ? <img src={mobileMembers.find((item) => (item.profile_id || item.profileId || item.id) === id).profile_img} alt="" /> : memberName(id).slice(0, 1).toUpperCase()}</span><b>{memberName(id)}</b></a>)}{Array.from({ length: Math.max(0, mobileCapacity - mobileRosterIds.length) }, (_, index) => <span className="flow-mobile-open-spot" key={`open-${index}`}><span><IonPersonAdd aria-hidden="true" /></span><b>Open spot</b></span>)}</div></div>}
          {current?.rosterError && <p className="flow-mobile-roster-signin" role="alert">{current.rosterError}</p>}
          <div className="flow-mobile-team-stats"><div><strong>{mobileTeamGames.filter((game) => game.status === "completed").length}</strong><span>Played</span></div><div><strong>{mobileTeamGames.filter((game) => game.status !== "completed").length}</strong><span>To play</span></div></div>
          {mobileUpcomingGames.length > 0 && <h3>Next games <small>{mobileUpcomingGames.length}</small></h3>}
          {[...mobileUpcomingGames, ...mobileResults].map((game, index) => <div key={game.id}>{index === mobileUpcomingGames.length && mobileResults.length > 0 && <h3>Results <small>{mobileResults.length}</small></h3>}<button type="button" className="flow-mobile-team-game" onClick={() => { setMobileTeamId(null); choose(game); }}><span className="flow-mobile-team-game-top"><small>{state.stages.find((stage) => stage.id === game.stage_id)?.label || "Tournament"} · {game.label}</small><b>{game.status === "completed" ? "FINAL" : "UPCOMING"}</b></span><span className="flow-mobile-team-game-versus"><span className="flow-mobile-team-game-side"><span className="flow-mobile-team-game-logo">{avatarFor(game.participant_a) && <img src={avatarFor(game.participant_a)} alt="" />}</span><strong className={game.participant_a === mobileTeamId ? "selected" : undefined}>{name(game.participant_a)}</strong></span><em>{game.status === "completed" ? `${game.score_a}–${game.score_b}` : "VS"}</em><span className="flow-mobile-team-game-side"><span className="flow-mobile-team-game-logo">{avatarFor(game.participant_b) && <img src={avatarFor(game.participant_b)} alt="" />}</span><strong className={game.participant_b === mobileTeamId ? "selected" : undefined}>{name(game.participant_b)}</strong></span></span><span className="flow-mobile-team-game-meta"><IonTime aria-hidden="true" /><span>{clock(game.start_minute)}</span><span>·</span><span>{game.court || "Area flexible"}</span>{game.duration_minutes != null && <><span>·</span><span>{game.duration_minutes} min</span></>}</span></button></div>)}
          {!mobileTeamGames.length && <p>No games yet. Games for {name(mobileTeamId)} will appear here when fixtures are set.</p>}
        </div>
      </section>}
      {mobileClubProfile && mobileTeamId && <section className="flow-mobile-profile" role="dialog" aria-modal="true" aria-labelledby="flow-mobile-profile-title" tabIndex={-1}>
        <div className="flow-mobile-profile-content">
          <header><button type="button" aria-label="Back to tournament games" onClick={() => setMobileClubProfile(false)}><IonChevronBack aria-hidden="true" /></button></header>
          <h2 id="flow-mobile-profile-title">{name(mobileTeamId)}</h2>
          <div className={`flow-mobile-profile-image${teamDisplayMode(mobileTeamDetails?.team_image || mobileTeamDetails?.image, mobileTeamDetails?.image_display_mode) === "logo" ? " logo" : ""}`}><img src={teamArtworkSrc(mobileTeamDetails?.team_image || mobileTeamDetails?.image, teamDisplayMode(mobileTeamDetails?.team_image || mobileTeamDetails?.image, mobileTeamDetails?.image_display_mode))} alt="" /></div>
          <div className="flow-mobile-profile-dashboard"><div className="flow-mobile-profile-season"><div><small>ATHLETE DASHBOARD</small><strong>{profileSeason || "Season"}</strong></div><img src={logo} alt="" /></div><div className="flow-mobile-profile-rank"><button type="button" onClick={() => setProfileRankMode((mode) => (mode + 1) % 3)}><small>{["GLOBAL", "NATIONAL", "REGIONAL"][profileRankMode]} RANK</small><strong>#{mobileTeamDetails?.[["global_rank", "national_rank", "regional_rank"][profileRankMode]] ?? 0}</strong></button><div><small>RATING</small><strong>{mobileTeamDetails?.rating ?? 30}</strong></div></div><div className="flow-mobile-profile-league"><strong>League {mobileTeamDetails?.league ?? 1}</strong><small>0 / 700</small><span /></div></div>
          <div className="flow-mobile-profile-stat-card"><h3>STATISTICS</h3><div className="flow-mobile-profile-stat-grid">{[
            { key: "played", title: "Played", value: mobileTeamDetails?.games_played ?? 0, accent: "#00B9FF" },
            { key: "won", title: "Won", value: mobileTeamDetails?.games_won ?? 0, accent: "#22D56A" },
            { key: "lost", title: "Lost", value: mobileTeamDetails?.games_lost ?? 0, accent: "#FF4B4B" },
            { key: "tournaments", title: profileStatModes.tournaments ? "Tourn. Played" : "Tourn. Won", value: mobileTeamDetails?.[profileStatModes.tournaments ? "tournaments_played" : "tournaments_won"] ?? 0, accent: "#C80C9F" },
            { key: "goals", title: "Goals", value: mobileTeamDetails?.goals ?? 0, accent: "#9747FF" },
            { key: "win", title: "Win %", value: `${mobileTeamDetails?.win_percentage ?? 0}%`, accent: "#D5A419" },
          ].map((stat) => <button key={stat.key} type="button" style={{ "--stat-accent": stat.accent }} onClick={() => setProfileStatModes((modes) => ({ ...modes, [stat.key]: ((modes[stat.key] || 0) + 1) % (stat.key === "tournaments" ? 2 : 3) }))}><strong>{stat.value}</strong><span>{stat.title}{stat.key !== "tournaments" ? ` · ${["All", "Comp", "Casual"][profileStatModes[stat.key] || 0]}` : ""}</span></button>)}</div></div>
          <div className="flow-mobile-profile-members"><h3>TEAM MEMBERS</h3><p>{mobileRosterIds.length}/{mobileCapacity} spots filled · {Math.max(0, mobileCapacity - mobileRosterIds.length)} open</p><div>{mobileRosterIds.map((id) => <a key={id} href={`/profile/${id}`} aria-label={`View ${memberName(id)} profile`}><span>{mobileMembers.find((item) => (item.profile_id || item.profileId || item.id) === id)?.profile_img ? <img src={mobileMembers.find((item) => (item.profile_id || item.profileId || item.id) === id).profile_img} alt="" /> : memberName(id).slice(0, 1).toUpperCase()}</span><b>{memberName(id)}</b></a>)}{Array.from({ length: Math.max(0, mobileCapacity - mobileRosterIds.length) }, (_, index) => <div key={`profile-open-${index}`}><span><IonPersonAdd aria-hidden="true" /></span><b>Open spot</b></div>)}</div></div>
        </div>
      </section>}
    </main>
  );
}
